import { Loading } from "../../../../../../react-components/lu-component/src/index";
import * as React from "react";
import { useParams } from "react-router-dom";
import { AccountWithoutPassword } from "../../../../../../server/models/account";
import { v3ApiEndpoint } from "../../../../../../server/router/endpoint";
import { GetSimulationV3Response } from "../../../../../../server/types/request/report/simulation";
import { SimulationLatestPage, SimulationPageProps } from "../../../../components/pages/report/latest/simulation";
import { ConfirmBox } from "../../../../components/parts/confirm-box";

import { get, makeError, makeErrorBlob, makeDwnloadImage, put, remove } from "../../../../lib/request";
import { reducer, initState } from "../../../../reducers/report/latest/simulation";
import { UpsertCP2Response } from "../../../../../../server/types/request/report/cp2";
import { isDefined } from "../../../../lib/data";

export const SimulationContainer = (loginAccount: AccountWithoutPassword) => {
	const [state, dispatch] = React.useReducer(reducer, initState);
	const { info, loading, ...sProps } = state;
	const { jobId } = useParams<{ jobId: string }>();
	const refetch = React.useCallback(() => {
		get<GetSimulationV3Response>(`${v3ApiEndpoint.simulation}/${jobId}`)
			.then((response) => {
				dispatch({ type: "loadData", payload: response.data });
			})
			.catch((error) => {
				dispatch({ type: "changeMessageInfo", payload: makeError(error) });
			});
	}, [jobId]);
	React.useEffect(() => refetch(), [refetch]);
	const onChangeRatio = React.useCallback<SimulationPageProps["onChangeRatio"]>((target, name, value) => {
		dispatch({ type: "changeRatio", payload: { target, name, value } });
	}, []);
	const onSimulation = React.useCallback<SimulationPageProps["onSimulation"]>(
		(conceptType) => {
			const html = document.getElementsByClassName("simulation-table").item(0).outerHTML;
			dispatch({ type: "changeLoading", payload: true });
			const correctionValue = sProps.simulationPredictionData[conceptType]?.correctionValue;
			makeDwnloadImage(
				`${v3ApiEndpoint.simulation}/${jobId}`,
				{ html, ...correctionValue },
				`${sProps.job.jobNum}_executive_summary.png`,
			)
				.then(() => dispatch({ type: "downloadSimulation", payload: true }))
				.catch(async (error) => {
					dispatch({ type: "changeMessageInfo", payload: await makeErrorBlob(error, true) });
				});
		},
		[sProps.simulationPredictionData, sProps.job?.jobNum, jobId],
	);

	const onSubmitPurchaseRate = React.useCallback<SimulationPageProps["onSubmitPurchaseRate"]>(() => {
		const cp = sProps.currentProduct?.correctionValue;
		if (!isDefined(cp)) return;
		const { purchaseRate } = cp;
		dispatch({ type: "changeLoading", payload: true });
		put<UpsertCP2Response>(`${v3ApiEndpoint.cpPurchaseRate}/${jobId}`, purchaseRate)
			.then((response) => dispatch({ type: "updateData", payload: response.data }))
			.catch((error) => {
				dispatch({ type: "changeMessageInfo", payload: makeError(error, true) });
			});
	}, [jobId, sProps.currentProduct?.correctionValue]);

	const onSubmitRecognitionRate = React.useCallback<SimulationPageProps["onSubmitRecognitionRate"]>(() => {
		const cp = sProps.currentProduct?.correctionValue;
		if (!isDefined(cp)) return;
		const { recognitionRate } = cp;
		dispatch({ type: "changeLoading", payload: true });
		put<UpsertCP2Response>(`${v3ApiEndpoint.cpRecognitionRate}/${jobId}`, recognitionRate)
			.then((response) => dispatch({ type: "updateData", payload: response.data }))
			.catch((error) => {
				dispatch({ type: "changeMessageInfo", payload: makeError(error, true) });
			});
	}, [jobId, sProps.currentProduct?.correctionValue]);
	const onChangeCP = React.useCallback<SimulationPageProps["onChangeCP"]>((name, value) => {
		dispatch({ type: "changeCP", payload: { name, value } });
	}, []);
	const onClearCP = React.useCallback(() => {
		remove<UpsertCP2Response>(`${v3ApiEndpoint.cp}/${jobId}`)
			.then((response) => dispatch({ type: "clearCPData", payload: response.data }))
			.catch((error) => {
				dispatch({ type: "changeMessageInfo", payload: makeError(error, true) });
			});
	}, [jobId]);
	const onSimulationSave = React.useCallback<SimulationPageProps["onSimulationSave"]>(
		(conceptType) => {
			dispatch({ type: "changeLoading", payload: true });
			const correctionValue = sProps.simulationPredictionData[conceptType].correctionValue;
			put<GetSimulationV3Response>(`${v3ApiEndpoint.simulation}/${jobId}`, correctionValue)
				.then(() => dispatch({ type: "saveSimulation", payload: { message: "保存が完了しました。", isSuccess: true } }))
				.catch((error) => {
					dispatch({ type: "saveSimulation", payload: makeError(error, true) });
				});
		},
		[sProps.simulationPredictionData, jobId],
	);
	const setChanged = React.useCallback((changed: boolean) => dispatch({ type: "setChanged", payload: changed }), []);
	return (
		<>
			<Loading loading={loading} />
			<ConfirmBox info={info} titleLabel="保存処理" />
			<SimulationLatestPage
				{...sProps}
				setChanged={setChanged}
				errors={info.errors}
				refetch={refetch}
				loginAccount={loginAccount}
				onChangeRatio={onChangeRatio}
				onSimulation={onSimulation}
				onSimulationSave={onSimulationSave}
				onSubmitRecognitionRate={onSubmitRecognitionRate}
				onClearCP={onClearCP}
				onSubmitPurchaseRate={onSubmitPurchaseRate}
				onChangeCP={onChangeCP}
			/>
		</>
	);
};
